import { Component } from 'react';
import { getEnvironment, slackNotificationErrors, circuitBreaker } from './_shared/services';

// Error tracking to prevent infinite loops
const errorTracker = {
  lastError: null,
  lastErrorTime: 0,
  errorCount: 0,
  maxErrorsPerMinute: 5,
  resetTimeout: 60000, // 1 minute
  errorsByComponent: new Map(), // Track errors by component
  componentErrorThreshold: 2, // Reduced threshold to catch loops faster
  globalErrorThreshold: 10, // Max total errors before forcing a page refresh
  lastRefreshTime: 0,
  minRefreshInterval: 300000, // 5 minutes - prevent excessive refreshes
};

export default class ErrorHandler extends Component {
	static getDerivedStateFromError(error) {
		// Update state to trigger fallback UI
		return { hasError: true, error };
	}

	constructor(props) {
		super(props);
		this.state = { hasError: false, error: null };
	}

	componentDidCatch(error, errorInfo) {
		// Check if we're getting too many errors too quickly
		const now = Date.now();
		const errorString = error.toString();
		const isSameError = errorString === errorTracker.lastError?.toString();
		
		// Extract component name from error info
		const componentStackLines = errorInfo.componentStack.split('\n');
		const componentName = componentStackLines.length > 1 ?
			componentStackLines[1].trim().replace(/^in\s+/, '') : 'Unknown';
		
		// Reset error count if it's been more than a minute since the last error
		if (now - errorTracker.lastErrorTime > errorTracker.resetTimeout) {
			errorTracker.errorCount = 0;
			errorTracker.errorsByComponent.clear();
		}
		
		// Track errors by component
		const componentErrors = errorTracker.errorsByComponent.get(componentName) || 0;
		errorTracker.errorsByComponent.set(componentName, componentErrors + 1);
		
		// Increment error count
		errorTracker.errorCount++;
		errorTracker.lastError = error;
		errorTracker.lastErrorTime = now;
		
		// Check if we're in an infinite loop for this component
		const isComponentInfiniteLoop =
			errorTracker.errorsByComponent.get(componentName) >= errorTracker.componentErrorThreshold;
		
		// Check if we're experiencing a global error storm
		const isGlobalErrorStorm = errorTracker.errorCount >= errorTracker.globalErrorThreshold;
		
		// Handle global error storm - force page refresh if we haven't refreshed recently
		if (isGlobalErrorStorm && now - errorTracker.lastRefreshTime > errorTracker.minRefreshInterval) {
			console.error(`Severe error storm detected! Forcing page refresh to recover.`);
			errorTracker.lastRefreshTime = now;
			
			// Set a flag to indicate we're forcing a refresh due to errors
			sessionStorage.setItem('errorHandler_forcedRefresh', 'true');
			sessionStorage.setItem('errorHandler_refreshTime', now.toString());
			
			// Force refresh after a short delay to allow logging
			setTimeout(() => {
				window.location.reload();
			}, 500);
			
			this.logErrorStorm();
			return;
		}
		
		// Only log and notify if we're not in an error storm
		if (errorTracker.errorCount <= errorTracker.maxErrorsPerMinute) {
			this.logError(error, errorInfo);
			
			// If we detect a component in an infinite loop, log a specific message
			if (isComponentInfiniteLoop) {
				console.error(`Possible infinite loop detected in component: ${componentName}`);
				console.error(`This may be caused by state updates during rendering or effects running repeatedly`);
				
				// Force break the loop by setting a flag in localStorage
				localStorage.setItem('errorHandler_breakLoop', componentName);
				
				// Log a more specific error storm for this component
				this.logComponentErrorStorm(componentName, errorString);
			}
		} else if (errorTracker.errorCount === errorTracker.maxErrorsPerMinute + 1) {
			// Log one final message about the error storm
			console.error(`Error storm detected! Suppressing further error notifications.`);
			this.logErrorStorm();
		}
	}
	
	logErrorStorm() {
		// Notify about the error storm
		if (this.reportError(errorTracker.lastError)) {
			slackNotificationErrors({
				username: 'errors',
				title: 'Error Storm Detected',
				message: `Multiple errors occurring rapidly. Suppressing further notifications to prevent API overload.`,
				location: window.location.href,
			}).catch(console.error);
		}
	}
	
	logComponentErrorStorm(componentName, errorString) {
		// Notify about a component-specific error storm (likely an infinite loop)
		if (this.reportError(errorTracker.lastError)) {
			slackNotificationErrors({
				username: 'errors',
				title: 'Infinite Loop Detected',
				message: `Component "${componentName}" is triggering errors repeatedly, likely in an infinite loop. Error: ${errorString}`,
				location: window.location.href,
			}).catch(console.error);
		}
	}

	// This function determines which errors should be reported to external services
	// It does NOT affect whether the error boundary shows the fallback UI
	reportError = (error_) => {
		console.log(error_);
		try {
			if (error_ === null) return false;
			
			// Don't report common React errors in development
			if (getEnvironment() === 'dev') return false;
			
			// Don't report in forced production mode
			if (String(process.env.REACT_APP_FORCE_PROD).toLowerCase() === 'true')
				return false;
			
			// Skip reporting for certain common error types
			const error = error_.toString();
			const skipReportingErrors = [
				'Invariant Violation',
				'TypeError',
				'ReferenceError',
			];
			
			for (const er of skipReportingErrors) {
				if (error.slice(0, er.length) === er) return false;
			}
	
			// Report all other errors
			return true;
		} catch (error) {
			console.log(error);
			return false;
		}
	};

	async logError(error, errorInfo) {
		let errorLoc = errorInfo.componentStack;
		errorLoc = errorLoc.slice(0, 1800);
		
		// Only report errors that pass our reporting filter
		if (this.reportError(error)) {
			try {
				await slackNotificationErrors({
					username: 'errors',
					title: 'Application Crash: Web',
					message: `${error.toString()} ${errorLoc}`,
					location: window.location.href,
				});
			} catch (notificationError) {
				console.error('Failed to send error notification:', notificationError);
			}
		}

		console.error(error, errorInfo);
	}

	refreshApp = () => {
		window.location.reload();
	};

	render() {
		// Check if we need to break an infinite loop
		const breakLoopComponent = localStorage.getItem('errorHandler_breakLoop');
		const forcedRefresh = sessionStorage.getItem('errorHandler_forcedRefresh');
		
		// Only render children if there's no error
		// We no longer use IgnoreErrors to determine whether to render children
		// This ensures all errors trigger the fallback UI and break potential infinite loops
		if (!this.state.hasError) {
			return <>{this.props.children}</>;
		}

		// Clear the break loop flag when rendering the error UI
		if (breakLoopComponent) {
			localStorage.removeItem('errorHandler_breakLoop');
		}
		
		// Clear the forced refresh flag when rendering the error UI
		if (forcedRefresh) {
			sessionStorage.removeItem('errorHandler_forcedRefresh');
		}

		// Render fallback UI
		return (
			<div className="error-wrapper">
				<div className="error-container text-center">
					<h1>Woops! Something Went Wrong.</h1>
					{breakLoopComponent && (
						<div style={{ color: 'red', margin: '10px 0' }}>
							<p>An infinite loop was detected in component: <strong>{breakLoopComponent}</strong></p>
							<p>This may be caused by state updates during rendering or effects running repeatedly.</p>
						</div>
					)}
					{forcedRefresh && (
						<div style={{ color: 'orange', margin: '10px 0' }}>
							<p>The application was automatically refreshed due to multiple errors.</p>
							<p>If this happens repeatedly, please contact support.</p>
						</div>
					)}
					<p style={{ fontSize: '1rem' }}>
						Try{' '}
						<a href="javascript:void(0)" onClick={this.refreshApp}>
							refreshing
						</a>{' '}
						the page. Please contact support if the issue persists.
					</p>
					<p style={{ fontSize: '0.9rem', color: '#666', marginTop: '20px' }}>
						Error details: {this.state.error && this.state.error.toString()}
					</p>
				</div>
			</div>
		);
	}
}
